import userData from '../user-data';
import type { Vehicle, TireSize as FitmentTireSize } from '../user-data';
import fitmentApi from './fitment-api';
import type { TireSelector } from './tireselector';
import { t } from '../i18n';
import { isInViewport } from '../utils';
import Mustache from 'mustache';
import AutocompleteCombobox from '../combobox/autocomplete-combobox';

export type State = {
  /** Manufacturer slug */
  manufacturer: string | null,
  /** Year */
  year: string | null,
  /** Model id (numeric) */
  model: string | null,
  /** Vehicle id (numeric) */
  vehicle: string | null,
};

class Model {
  // Needed DOM elements
  dom: {
    root: HTMLElement,
    form: HTMLFormElement,
    formSubmitBtn: HTMLButtonElement,
    manufacturer: HTMLSelectElement,
    year: HTMLSelectElement,
    model: HTMLSelectElement,
    vehicle: HTMLSelectElement,
    errorMessage: HTMLElement,
    sizesContainer: HTMLElement,
  };

  // Tyre selector filter form state
  #state: State = {
    manufacturer: null,
    year: null,
    model: null,
    vehicle: null,
  };

  // Only ids are stored in persistent storage, names are cached here for display purposes
  #idNameCache: {
    manufacturer?: Record<string, string>,
    year?: Record<string, string>,
    model?: Record<string, string>,
    vehicle?: Record<string, string>,
  } = {};

  #loading = false;

  #comboboxes:any = {};
  #updatingFilters = false;

  parent: TireSelector;
  availableSizes: FitmentTireSize[] = [];
  name = 'Model';

  constructor(element: HTMLElement, parent: TireSelector) {
    this.dom = {
      root: element,
      form: element.querySelector('form[name="modelSearchForm"]') as HTMLFormElement,
      formSubmitBtn: element.querySelector('button[type="submit"]') as HTMLButtonElement,
      manufacturer: element.querySelector('select[name="manufacturer"]') as HTMLSelectElement,
      year: element.querySelector('select[name="year"]') as HTMLSelectElement,
      model: element.querySelector('select[name="model"]') as HTMLSelectElement,
      vehicle: element.querySelector('select[name="vehicle"]') as HTMLSelectElement,
      errorMessage: element.querySelector('[data-error-message]') as HTMLElement,
      sizesContainer: element.querySelector('[data-sizes-container]') as HTMLElement,
    };

    this.parent = parent;
    this.name = `${this.parent.name} ${this.name}`;

    if (this.parent && Object.keys(this.dom).every(key => this.dom[key])) {
      // Necessary data and dom elements found
      console.log(`[${this.name}] init()`);
      this.log('init settings', this);
      this.initState();
      this.initCustomDropdowns();
      this.attachEventListeners();
    } else {
      console.error(`[${this.name}]: missing data or dom elements`, this);
    }
  }

  /**
   * Initialises state from server or userData if available
   */
  protected initState() {
    // Default state is empty
    const initialState = this.getState();

    // Check values from select elements (values set by server)
    Object.keys(this.#state).forEach(key => {
      if(this.dom[key]) {
        const value = this.getSelectValue(this.dom[key]);
        initialState[key] = value;

        // Set all except manufacturer to disabled initially
        if (key !== 'manufacturer') {
          this.dom[key].disabled = true;
        }
      }
    });

    // Restore state from userData
    this.restoreStateFromUserData(initialState);

    this.setState(initialState);
  }

  /**
   * Restores state from persistent storage (userData).
   * Override in subclasses to disable or customize restoration.
   */
  protected restoreStateFromUserData(state: State) {
    const userVehicle = userData.get('vehicle');
    if(userVehicle) {
      Object.keys(userVehicle).forEach(key => {
        if(this.dom[key]) {
          state[key] = userVehicle[key];
        }
      });
    }
  }

  /**
   * Returns current state of size filters
   */
  getState() {
    return {...this.#state};
  }

  /**
   * Sets new state and updates form fields accordingly:
   * Sets available options for each field based on other field values.
   * Sets enabled/disabled state of fields.
   * Sets submit button enabled/disabled state.
   */
  async setState(newState: State) {
    const oldState = this.getState();
    const futureState = { ...oldState, ...newState };

    this.log('updating state', { oldState, futureState, changed: newState });

    // Set internal state
    this.#state = futureState;
    let manufacturerChanged = false;

    // Populate select options (manufacturer options come from server intitially)
    if (this.#state.manufacturer !== oldState.manufacturer) {
      manufacturerChanged = true;
      this.clearSelectOptions(this.dom.year);
      this.clearSelectOptions(this.dom.model);
      this.clearSelectOptions(this.dom.vehicle);
      this.clearSizeOptions();

      if(this.#state.manufacturer) {
        this.dom.manufacturer.value = this.#state.manufacturer;
        this.#cacheName(this.dom.manufacturer);
        this.setSelectLoading(this.dom.year, true);
        try {
          const years = await fitmentApi.getYears(this.#state.manufacturer);
          years.sort((a, b) => parseInt(b.id) - parseInt(a.id));
          this.addSelectOptions(this.dom.year, years);
        } catch (error) {
          console.error(`[${this.name}] Error fetching years data:`, error);
        } finally {
          this.setSelectLoading(this.dom.year, false);
        }
      } else {
        this.dom.manufacturer.value = '';
      }

      // Combobox needs this to update correct values
      this.#updatingFilters = true;
      this.dom.manufacturer.dispatchEvent(new Event('change', { bubbles: true }));
      this.#updatingFilters = false;
    }

    // Switching manufacturer but choosing same year should still trigger year change to fetch models for new manufacturer
    if (manufacturerChanged || this.#state.year !== oldState.year) {
      this.clearSelectOptions(this.dom.model);
      this.clearSelectOptions(this.dom.vehicle);
      this.clearSizeOptions();

      if(!this.#state.year) {
        this.dom.year.value = '';
      }

      if(this.#state.manufacturer && this.#state.year) {
        this.dom.year.value = this.#state.year;
        this.#cacheName(this.dom.year);
        this.setSelectLoading(this.dom.model, true);

        try {
          const models = await fitmentApi.getModels({
            manufacturerId: this.#state.manufacturer,
            year: this.#state.year,
          });
          this.addSelectOptions(this.dom.model, models);
        } catch (error) {
          console.error(`[${this.name}] Error fetching models data:`, error);
        } finally {
          this.setSelectLoading(this.dom.model, false);
        }
      }
    }

    if (this.#state.model !== oldState.model) {
      this.clearSelectOptions(this.dom.vehicle);

      if(this.#state.manufacturer && this.#state.year && this.#state.model) {
        this.dom.model.value = this.#state.model;
        this.#cacheName(this.dom.model);
        this.setSelectLoading(this.dom.vehicle, true);

        try {
          const vehicles = await fitmentApi.getVehicles({
            manufacturerId: this.#state.manufacturer,
            year: this.#state.year,
            modelId: this.#state.model,
          });
          this.addSelectOptions(this.dom.vehicle, vehicles);

          if(this.dom.sizesContainer.hidden === false) {
            // If sizes section is visible and model changes, need to update it
            // basically autosubmit
            this.dom.form.requestSubmit();
          }

        } catch (error) {
          console.error(`[${this.name}] Error fetching vehicles data:`, error);
        } finally {
          this.setSelectLoading(this.dom.vehicle, false);
        }
      }
    }

    if (this.#state.vehicle !== oldState.vehicle) {
      if(this.#state.manufacturer && this.#state.year && this.#state.model && this.#state.vehicle) {
        this.dom.vehicle.value = this.#state.vehicle;
        this.#cacheName(this.dom.vehicle);
      }
    }

    // Enable/disable submit button (vehicle is optional)
    if(this.#state.manufacturer && this.#state.year && this.#state.model) {
      this.dom.formSubmitBtn.disabled = false;
    } else {
      this.dom.formSubmitBtn.disabled = true;
    }
  }

  setSelectLoading(select: HTMLSelectElement, state: boolean) {
    if (state) {
      select.classList.add('loading');
    } else {
      select.classList.remove('loading');
    }
  }

  clearSelectOptions(select: HTMLSelectElement) {
    // Clear all but the first empty option
    Array.from(select.options).filter((opt) => opt.value !== '').forEach((opt) => {
      opt.remove();
    });

    select.value = '';
    select.disabled = true;
  }

  addSelectOptions(select: HTMLSelectElement, options: Array<{id: string; name: string;}>) {
    options.forEach((optionData) => {
      const option = new Option(optionData.name, optionData.id, false);
      select.add(option);
    });

    select.disabled = false;
  }

  #cacheName(select: HTMLSelectElement) {
    const selectName = select.name;
    const selectedOption = select.options[select.selectedIndex];
    if(selectedOption) {
      if (!this.#idNameCache[selectName]) {
        this.#idNameCache[selectName] = {};
      }
      this.#idNameCache[selectName]![selectedOption.value] = selectedOption.text;
    }
  }

  protected attachEventListeners() {
    this.dom.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.dom.form.addEventListener('reset', this.onFormReset.bind(this));
    this.dom.manufacturer.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.year.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.model.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.vehicle.addEventListener('change', this.onSelectChange.bind(this));

    // Delegated events for elements created dynamically
    this.dom.sizesContainer.addEventListener('change', (event) => {
      const target = event.target as HTMLSelectElement;

      if (target.matches('select[name="engine"]')) {
        // Engine select event inside sizes container.
        // This is available if no vehicle was selected in form previously.
        const value = target.value || '';

        if(value) {
          const oldState = this.getState();
          this.setState({
            ...oldState,
            vehicle: value
          }).then(() => {
            // Trigger form submit automatically
            this.dom.form.requestSubmit();
          })
        }
      } else if(target.matches('select[name="size"]')) {
        // Size select event inside sizes container
        const sizeStr = target.value || '';

        if(sizeStr) {
          this.parent.onSizeSelect(sizeStr);
        }
      }
    });

    // Subscribe to user data changes
    this.subscribeToUserData();
  }

  /**
   * Subscribes to external userData changes.
   * Override in subclasses to disable or customize subscription.
   */
  protected subscribeToUserData() {
    userData.subscribe('vehicle', this.onUserDataChange.bind(this));
  }

  protected initCustomDropdowns() {
    this.#comboboxes.manufacturer = new AutocompleteCombobox({
      select: this.dom.manufacturer,
      cssClassPrefix: 'combobox',
      browserAutocomplete: 'off'
    });

    this.#comboboxes.model = new AutocompleteCombobox({
      select: this.dom.model,
      cssClassPrefix: 'combobox',
      browserAutocomplete: 'off'
    });
  }

  /**
   * Formats select value to string or null
   * All values are strings in select elements, make empty string null and others as strings
   */
  getSelectValue(select: HTMLSelectElement) {
    return select.value === '' ? null : String(select.value);
  }

  processTireSizes(vehicleTireSizes: Array<FitmentTireSize>): FitmentTireSize[] {
    this.log('processTireSizes()', vehicleTireSizes);

    // Match with available NT tires
    const availableSizes: FitmentTireSize[] = [];
    vehicleTireSizes.forEach(vSize => {
      // Check availability by comparing to sizeData
      const matches = this.parent.settings.sizeData.filter(item => {
        return vSize.size === item.size;
      });

      if(matches.length) {
        availableSizes.push(vSize);
      }
    });

    availableSizes.sort((a, b) => {
      // Sort by rim, width, height
      if (a.rim !== b.rim) {
        return a.rim - b.rim;
      } else if (a.width !== b.width) {
        return a.width - b.width;
      } else {
        return a.height - b.height;
      }
    });

    this.log('available sizes for vehicle', {vehicleTireSizes, availableSizes});

    return availableSizes;
  }

  showSizeOptions() {
    this.log('showSizeOptions()', this.availableSizes);

    const carModel = [
      this.#idNameCache.manufacturer?.[this.#state.manufacturer!],
      this.#idNameCache.model?.[this.#state.model!],
      this.#idNameCache.year?.[this.#state.year!],
      this.#idNameCache.vehicle?.[this.#state.vehicle!],
    ].filter(Boolean).join(', ');

    const templateData = {
      carModel,
      noVehicle: !this.#state.vehicle,
      rimSizeRange: '',
      speedRatingRange: '',
      sizes: this.availableSizes,
      sizesPlaceholder: t(`${this.parent.settings.lang}.tireselector__result__choose_size__placeholder`, {amount: this.availableSizes.length}),
      engineOptions: [] as Array<{id: string; name: string;}>,
    };

    const noVehicle = !this.#state.vehicle;

    if(noVehicle && this.availableSizes.length) {
      // Get rim size range

      const rimSizes = this.availableSizes.map(size => size.rim).sort();
      const rimSizeRange = `${rimSizes[0]}${rimSizes.length > 1 ? `-${rimSizes[rimSizes.length - 1]}` : ''}`;

      templateData.rimSizeRange = rimSizeRange;

      // Get speed rating range

      const speedRatingCodes = new Set<string>();
      this.availableSizes.forEach(availableSize => {
        // Find speed ratings for this size from all sizes
        const matchingSizes = this.parent.settings.sizeData.filter(size => {
          return size.size === availableSize.size;
        });

        matchingSizes.forEach(size => {
          speedRatingCodes.add(size.speedIndex);
        });
      });

      // Get speed rating range by code set (slowest and fastest)
      const selectedRatings = this.parent.settings.speedRatings.filter(rating => speedRatingCodes.has(rating.code));
      selectedRatings.sort((a, b) => a.speed - b.speed);
      const lowest = selectedRatings[0];
      const highest = selectedRatings[selectedRatings.length - 1];
      const speedRatingRange = `${lowest.code} ${lowest.speed} - ${highest.code} ${highest.speed} ${highest.unit}`;

      templateData.speedRatingRange = speedRatingRange;

      // Get engine options
      // Just copy vehicle dom select options
      const engineOptions: Array<{id: string; name: string;}> = [];
      Array.from(this.dom.vehicle.options).forEach(option => {
        if(option.value !== '') {
          engineOptions.push({
            id: option.value,
            name: option.text,
          });
        }
      });

      templateData.engineOptions = engineOptions;
    }

    this.log('Rendering sizes template with data:', templateData);

    this.dom.sizesContainer.innerHTML = Mustache.render(this.parent.sizesTemplate, templateData);
    this.dom.sizesContainer.hidden = false;

    if(!isInViewport(this.dom.sizesContainer)) {
      this.dom.sizesContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  clearSizeOptions() {
    this.dom.sizesContainer.innerHTML = '';
    this.dom.sizesContainer.hidden = true;
  }

  showErrorMessage(message: string) {
    if(!message) {
      return;
    }

    const textContainer = this.dom.errorMessage.querySelector('[data-error-message-text]');

    if(textContainer) {
      textContainer.textContent = message;
    } else {
      this.dom.errorMessage.textContent = message;
    }

    this.dom.errorMessage.removeAttribute('hidden');
  }

  hideErrorMessage() {
    const textContainer = this.dom.errorMessage.querySelector('[data-error-message-text]');

    if(textContainer) {
      textContainer.textContent = '';
    } else {
      this.dom.errorMessage.textContent = '';
    }

    this.dom.errorMessage.setAttribute('hidden', 'hidden');
  }

  onSelectChange(event:Event) {
    // Callback was caused by internal change in filters
    if (this.#updatingFilters) {
      return;
    }

    const select = event.target as HTMLSelectElement;
    const selectName = select.name;
    const selectValue = this.getSelectValue(select);

    this.log('onSelectChange()', {selectName, selectValue});

    const newState = this.getState();
    let resetFields = false;
    for (let key in newState) {
      if (selectName === key) {
        newState[selectName] = selectValue;
        resetFields = true;
      } else if (resetFields) {
        newState[key] = null;
      }
    }

    this.setState(newState);
  }

  onFormSubmit(event:Event) {
    event.preventDefault();

    if(this.#loading) {
      return;
    }

    this.log('onFormSubmit()');

    this.#loading = true;
    this.parent.setLoading(true);

    this.persistVehicleSelection();

    const state = this.getState();
    this.parent.sendAnalyticsEvent({
      event: 'tyre_search_submit',
      tyre_search_type: 'Model Search',
      vehicle_manufacturer: this.getManufacturerName() || state.manufacturer,
      vehicle_year: this.getYearName() || state.year,
      vehicle_model: this.getModelName() || state.model,
      vehicle_engine: this.getVehicleName() || state.vehicle,
    });

    if(state.vehicle) {

      fitmentApi.getTireSizesByVehicle(state.vehicle)
        .then((data) => {
          this.log('got tire sizes for vehicle', data);
          this.availableSizes = this.processTireSizes(data);
          this.persistAvailableSizes(this.availableSizes);

          if(this.parent.settings.autoRedirect) {
            // If engine selected, dont show sizeOptions, but redirect to factory default size
            const selectedSize = this.availableSizes.find(size => size.default === true) || this.availableSizes[0];
            if(selectedSize?.size) {
              this.parent.onSizeSelect(selectedSize.size);
            } else {
              console.error(`[${this.name}]: no available sizes found for vehicle`);
              this.showSizeOptions(); // This handles also displaying message if no sizes found
            }
          } else {
            this.showSizeOptions();
          }
        })
        .catch((error) => {
          console.error(`[${this.name}]: failed to get tire sizes for vehicle`, error.message);
          const message = error.message || 'Failed to retrieve tire sizes. Please try again later.';
          this.showErrorMessage(message);
        })
        .finally(() => {
          this.#loading = false;
          this.parent.setLoading(false);
        });

        return;

    } else if(state.model && state.year) {

      fitmentApi.getTireSizesByModel({
        modelId: state.model,
        year: state.year,
      })
        .then((data) => {
          this.log('got tire sizes for model', data);
          this.availableSizes = this.processTireSizes(data);
          this.persistAvailableSizes(this.availableSizes);
          this.showSizeOptions();
        })
        .catch((error) => {
          console.error(`[${this.name}]: failed to get tire sizes for model`, error);
          const message = error.message || 'Failed to retrieve tire sizes. Please try again later.';
          this.showErrorMessage(message);
        })
        .finally(() => {
          this.#loading = false;
          this.parent.setLoading(false);
        });
    }
  }

  /**
   * Form reset clears user's data.
   * Clearing saved vehicle causes an UI update of this form.
   */
  onFormReset(event:Event) {
    this.clearUserData();
  }

  /**
   * Persists vehicle selection to userData.
   * Override in subclasses to disable or customize persistence.
   */
  protected persistVehicleSelection() {
    userData.store({
      vehicle: {
        manufacturer: this.getState().manufacturer!,
        manufacturerName: this.getManufacturerName(),
        year: this.getState().year!,
        model: this.getState().model!,
        modelName: this.getModelName(),
        vehicle: this.getState().vehicle || null,
        vehicleName: this.getVehicleName(),
      },
      vehicleNA: null,
      license: null,
      availableSizes: null,
      selectedSize: null,
    });
  }

  /**
   * Persists available sizes to userData.
   * Override in subclasses to disable or customize persistence.
   */
  protected persistAvailableSizes(sizes: FitmentTireSize[]) {
    userData.store({ availableSizes: sizes });
  }

  /**
   * Clears all persisted data from userData.
   * Override in subclasses to disable or customize clearing.
   *
   * Customer request that all data should be cleared, not just vehicle data.
   * Clearing saved vehicle causes an UI update of this form.
   */
  protected clearUserData() {
    userData.store({license: null, vehicle: null, vehicleNA: null, availableSizes: null, selectedSize: null, selectedProduct: null});
  }

  // Helper methods for accessing cached names (used by persistVehicleSelection, analytics)
  getManufacturerName(): string | null {
    const state = this.getState();
    return this.#idNameCache.manufacturer?.[state.manufacturer!] || null;
  }

  getModelName(): string | null {
    const state = this.getState();
    return this.#idNameCache.model?.[state.model!] || null;
  }

  getVehicleName(): string | null {
    const state = this.getState();
    return this.#idNameCache.vehicle?.[state.vehicle!] || null;
  }

  getYearName(): string | null {
    const state = this.getState();
    return this.#idNameCache.year?.[state.year!] || null;
  }

  onUserDataChange(vehicleData: Vehicle) {
    this.log('onUserDataChange(): user data changed', vehicleData);

    const currentState = this.getState();

    if(vehicleData === null) {
      // Clear state
      for (let key in currentState) {
        currentState[key] = null;
      }

      this.setState(currentState);
      this.clearSizeOptions();
    } else {
      // Don't set state if nothing has changed
      if(Object.keys(currentState).every(key => currentState[key] === vehicleData[key])) {
        return;
      }

      this.setState({
        manufacturer: vehicleData?.manufacturer || null,
        year: vehicleData?.year || null,
        model: vehicleData?.model || null,
        vehicle: vehicleData?.vehicle || null,
      });

      this.clearSizeOptions();
    }

  }

  log(message: string, ...optionalParams: any[]) {
    console.log(`[${this.name}] ${message}`, ...optionalParams, this.dom.root);
  }
}

export default Model;
